import type { RecoveryBatch } from '@/api/endpoints/channelRecovery'
import { isApiError } from '@/lib/errors'

/** Por que um vídeo não entrou na seleção (``excluded_reason`` da prévia). */
export const EXCLUDED_REASON_LABEL: Record<string, string> = {
  scheduled: 'agendado',
  too_recent: 'recente demais',
  in_active_batch: 'já num lote ativo',
  no_stats: 'sem estatística',
  destination_pending: 'post de destino pendente',
  from_pool: 'veio de pool',
}

const STATUS_LABEL: Record<RecoveryBatch['status'], string> = {
  queued: 'na fila',
  running: 'rodando',
  waiting: 'aguardando',
  paused: 'pausado',
  failed: 'falhou',
  done: 'concluído',
  undone: 'desfeito',
}

function hhmm(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

/** O estado do lote em uma linha: "rodando · 3/10", "aguardando cota até 04:30"… */
export function batchProgressLabel(batch: RecoveryBatch): string {
  const done = (batch.counts['done'] ?? 0) + (batch.counts['undone'] ?? 0)
  const progress = `${String(done)}/${String(batch.total)}`
  const undo = batch.mode === 'undo' ? ' (desfazendo)' : ''
  if (batch.status === 'waiting') {
    if (batch.wait_reason === 'interrupted') return `interrompido, retomando · ${progress}`
    if (batch.wait_reason === 'youtube_unavailable') return `aguardando o YouTube · ${progress}`
    if (batch.wait_reason === 'budget' || batch.wait_reason === 'quota') {
      const until = batch.resume_at ? ` até ${hhmm(batch.resume_at)}` : ''
      return `aguardando cota${until} · ${progress}`
    }
    return `aguardando · ${progress}`
  }
  return `${STATUS_LABEL[batch.status]}${undo} · ${progress}`
}

export function isBatchActive(batch: RecoveryBatch): boolean {
  return batch.status === 'queued' || batch.status === 'running' || batch.status === 'waiting'
}

/** Retomar: lote parado com algo a fazer (pendente ou interrompido no meio). */
export function canResume(batch: RecoveryBatch): boolean {
  if (isBatchActive(batch) || batch.action !== 'archive_weak') return false
  const todo =
    batch.mode === 'undo'
      ? (batch.counts['done'] ?? 0) +
        (batch.counts['in_flight'] ?? 0) +
        (batch.counts['failed'] ?? 0)
      : (batch.counts['pending'] ?? 0) + (batch.counts['in_flight'] ?? 0)
  return todo > 0
}

export function canUndo(batch: RecoveryBatch): boolean {
  return !isBatchActive(batch) && batch.action === 'archive_weak' && batch.status !== 'undone'
}

/** O backend ainda sem as rotas (o front publica antes do pull do skab). */
export function isMissingOnServer(err: unknown): boolean {
  return isApiError(err) && err.status === 404
}
